import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Many, orderBy } from "lodash";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { Helmet } from "react-helmet";
import { userFirestoreCollectionOnce } from "../../../hooks/useFirestoreCollection";
import {
  fetchCategory81497FromFirestore,
  fetchCity83431FromFirestore,
  fetchHotel28355FromFirestore,
  getTravelPackages,
} from "../../../firestore/firestoreService";
import { listenToPackages } from "../../../redux/data/package/packageActions";
import {
  ItemNotFound,
  PackageItemList,
  PackageListFrontEndWrapper,
} from "../../molecules";
import { listenToCategories } from "../../../redux/data/category/categoryActions";
import { listenToHotels } from "../../../redux/data/hotel/hotelActions";
import PackageFilter from "../../molecules/Packages/PackageFilter";
import { listenToCities } from "../../../redux/data/city/cityActions";

const defaultSorting: any = {
  type: "sorting",
  name: "minPricePerAdult",
  order: "asc",
  opr: "asc",
};
const PAGE_SIZE = 50; // items per page
export function paginate(page: number, pageSize: number) {
  if (page < 1 || pageSize < 1) return [];

  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return { start, end };
}
export const PackageList: React.FC<{
  params: any;
  partner?: string;
}> = ({ params, partner }) => {
  const { packages, totalPackages } = useSelector(
    (state: any) => state.packages,
  );
  const { cities } = useSelector((state: any) => state.cities);
  const { categories } = useSelector((state: any) => state.categories);
  const { hotels } = useSelector((state: any) => state.hotels);
  const { error } = useSelector((state: any) => state.async);
  const [showFilter, setShowFilters] = useState(true);
  const [sorting, setSorting] = useState<any>(defaultSorting);
  const [page, setPage] = useState(1);
  const [loadingPage, setLoading] = useState(true);
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1200px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });
  const [interest, setInterest] = React.useState("");
  const dispatch = useDispatch();
  React.useEffect(() => {
    setLoading(true);

    const qp = new URLSearchParams(location.search);

    getTravelPackages({
      type: qp.get("type") ?? "all",
      from: qp.get("from") ?? "all",
      to: qp.get("to") ?? "all",
      hotels: qp.get("hotels") ?? "all",
      when: qp.get("when") ?? "all",
      price: qp.get("price") ?? "",
      category: qp.get("category") ?? "all",
      interest,
      sorting, // same object you already have
      pageSize: 20,
      paginate: paginate(page, PAGE_SIZE),
      partner: partner ? partner : "POUPAS",
    })
      .then(({ data }: any) => {
        dispatch(listenToPackages(data.page, data.total)); // or your fetchPage logic
      })
      .finally(() => setLoading(false));
  }, [location.search, sorting, page, interest]);

  userFirestoreCollectionOnce({
    query: () =>
      fetchCategory81497FromFirestore([
        {
          type: "filter",
          name: "hasPackages",
          opr: "==",
          value: true,
        },
      ]),
    data: (data: any) => listenToCategories(orderBy(data, ["name"])),
    deps: [dispatch],
  });
  userFirestoreCollectionOnce({
    query: () =>
      fetchHotel28355FromFirestore([
        {
          type: "sorting",
          name: "name",
          opr: "asc",
          order: "asc",
        },
        {
          type: "filter",
          name: "topHotel",
          opr: "==",
          value: true,
        },
      ]),
    data: listenToHotels,
    deps: [dispatch],
  });
  userFirestoreCollectionOnce({
    query: () =>
      fetchCity83431FromFirestore([
        {
          type: "filter",
          name: "hasPackages",
          opr: "==",
          value: true,
        },
      ]),
    data: (data: any) => listenToCities(orderBy(data, ["name"])),
    deps: [dispatch],
  });
  const handelShowFilter = () => {
    setShowFilters((v) => !v);
  };
  const handelSortingChange = (value: string) => {
    const [name, direction]: [string, Many<boolean | "asc" | "desc">] =
      Object.values(value.split("-")) as [
        string,
        Many<boolean | "asc" | "desc">,
      ];
    setSorting({ type: "sorting", name, opr: direction, order: direction });
  };
  React.useEffect(() => {
    if (!isTabletOrMobile) {
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  }, [isTabletOrMobile]);
  if (error) {
    return (
      <ItemNotFound
        redirectTo="/"
        redirectBtnText="Back to Home Page"
        itemType="City"
        itemId={"sd"}
      />
    );
  }
  return (
    <PackageListFrontEndWrapper>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`Poupas for holidays - Packages List`}</title>
      </Helmet>
      <div className="package-list-frontend">
        <div className="package-list-frontend-body">
          <div
            className={classNames("package-list-frontend-left", {
              hide: showFilter,
            })}
          >
            <PackageFilter
              cities={cities.filter((v: any) => !v?.isBackend)}
              categories={categories}
              isTabletOrMobile={isTabletOrMobile}
              showFilter={handelShowFilter}
              setPage={setPage}
              params={params}
              hotels={hotels}
              url={"packages"}
            />
          </div>

          <PackageItemList
            travelPackages={packages}
            sorting={sorting}
            loading={loadingPage}
            isTabletOrMobile={isTabletOrMobile}
            isMobile={isMobile}
            onSort={handelSortingChange}
            onShowFilter={handelShowFilter}
            partner={partner}
            setPage={setPage}
            setInterest={setInterest}
            totalPackages={totalPackages}
            interest={interest}
            pageNumber={page}
          />
        </div>
      </div>
    </PackageListFrontEndWrapper>
  );
};
